// 10s ceiling on the two-step livestream-url resolution. Without it a
// stalled streamon.fm host would hang the promise indefinitely, which
// in turn freezes the player UI.
// (No numeric separator — Babel 6 / preset-es2015 predates ES2021.)
const LIVE_STREAM_TIMEOUT_MS = 10000;

/**
 * Viblast either doesn't like all the redirects that happen in our live stream url
 * or doesn't like something about one of the urls returned, so we get a sanitized
 * version of the livestream ourselves.
 * @param {String} station station name
 * @return {Promise} with URL of Empire hosted livestream or error
 */
export const getLiveStreamUrl = station => {  // eslint-disable-line arrow-body-style
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`getLiveStreamUrl: timed out after ${LIVE_STREAM_TIMEOUT_MS}ms`));
    }, LIVE_STREAM_TIMEOUT_MS);

    fetch(`https://${station}.streamon.fm/hls/${station.toUpperCase()}.m3u8`, { signal: controller.signal })
    .then(resp => resp.text())
    .then(data => {
      const regEx = /http.*\.m3u8/;
      const match = regEx.exec(data);
      if (!match) {
        clearTimeout(timer);
        reject(new Error('getLiveStreamUrl: no stream URL in m3u8 manifest'));
        return;
      }
      const nextUrl = match[0];

      fetch(nextUrl, { signal: controller.signal })
        .then(resp2 => resp2.url)
        .then(url => { clearTimeout(timer); resolve(url); })
        .catch(e2 => { clearTimeout(timer); reject(e2); });
    })
    .catch(e => { clearTimeout(timer); reject(e); });
  });
};


// Build the share/listen page URL for a podcast episode, attaching the
// `smc` (social-media-connection) attribution param only when it's a real
// value. A zoneless widget embed leaves `smc` undefined; the old
// `...html?smc=${smc}` stringified that to the literal "undefined", which
// the backend then fed into an integer id lookup and 500'd
// (ValueError: Field 'id' expected a number but got 'undefined').
export const buildPlayerUrl = (podcastUrl, slug, podcastId, smc) => {
  const url = `${podcastUrl}/playlist/${slug}-${podcastId}.html`;
  // Falsy smc (undefined / null / '') means "no attribution" — omit it.
  // Production always passes a non-empty digit string, so a string like
  // '0' is kept; only a literal numeric 0 (never emitted) would drop.
  return smc ? `${url}?smc=${smc}` : url;
};


const HTTP_PROTOCOLS = new Set(['http:', 'https:']);
const HTTP_SCHEME = /^https?:\/\//i;
// Self-hosted inline-image placeholders (P1-18 placeholderImage) are data:image
// URIs. Safe to permit here: urlGetter's output only ever feeds `<img src>`,
// where the browser loads SVG in restricted mode — no script execution, no
// external fetch. Deliberately scoped to data:image/* (NOT a blanket data:, so
// no data:text/html etc.).
const DATA_IMAGE = /^data:image\//i;

// Resolve a (possibly relative) link against an api host, blocking anything
// that isn't http(s) or a data:image/* URI. Returns '' for unsafe or
// unparseable input — caller can fall back to a placeholder image / a no-op
// href.
//
// Why parse with `new URL` rather than regex-only: the WHATWG URL parser
// strips leading C0 controls and ASCII whitespace before scheme detection,
// so a regex anchored at /^/ misses inputs like " javascript:..." or
// "\tjavascript:...". Letting the URL constructor do scheme detection means
// our protocol allowlist is the only gatekeeper.
export const urlGetter = (apiPrefix, link) => {
  if (!link) return '';

  // Try as an absolute URL first.
  try {
    const absolute = new URL(link);
    if (HTTP_PROTOCOLS.has(absolute.protocol)) return absolute.toString();
    // Return the original data:image/* URI verbatim (don't round-trip through
    // URL.toString(), which would re-encode the SVG payload).
    if (absolute.protocol === 'data:' && DATA_IMAGE.test(link)) return link;
    return '';
  } catch (_) {
    // not absolute; fall through to relative resolution
  }

  const base = HTTP_SCHEME.test(apiPrefix) ? apiPrefix : `https://${apiPrefix}`;
  try {
    const resolved = new URL(link, base);
    return HTTP_PROTOCOLS.has(resolved.protocol) ? resolved.toString() : '';
  } catch (_) {
    return '';
  }
};
